import React, {useEffect, useState} from "react";
import {Box, Stack, Typography} from "@mui/material";
import {toast} from "react-toastify";
import {CampaignRepository} from "../../../repositories/CampaignRepository";
import LoadingScreen from "../../../components/loading-screen/LoadingScreen";
import useConfirm from "../../../hooks/useConfirm";
import {ACTIVE_POLL_INTERVAL} from "../../project/campaigns/constants";
import {useCampaignPhoneNumbers} from "../../project/campaigns/hooks/useCampaignPhoneNumbers";
import {usePolling} from "../../project/campaigns/hooks/usePolling";
import {useAmiExtensions} from "../../project/campaigns/hooks/useAmiExtensions";
import {CampaignDetailsHeaderNew} from "./components/campaign-details/CampaignDetailsHeaderNew";
import {CampaignSummaryNew} from "./components/campaign-details/CampaignSummaryNew";
import {CampaignPhoneNumbersTableNew} from "./components/campaign-details/CampaignPhoneNumbersTableNew";
import {CallAttemptsDialogNew} from "./components/campaign-details/CallAttemptsDialogNew";
import {CampaignStatusChipsNew} from "./components/CampaignStatusChipsNew";
import {CampaignFormDialogNew} from "./components/campaign-form/CampaignFormDialogNew";

const callErrorMessage = (error) => {
  if (error?.response?.status === 409) return "This number can't be called now: a call is in progress, it already answered, or it has no attempts left";
  if (error?.response?.status === 400) return "The campaign needs a from number and an extension before calling";
  return error?.response?.data?.message || "Failed to place the call";
};

// A campaign with its phone numbers and their calls, for registry projects: registryProjectId /
// subProjects give the extensions (from the campaign's language) and populate the edit form.
export const CampaignDetailsNew = ({campaignId, registryProjectId, subProjects, onBack}) => {
  const [campaign, setCampaign] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [attemptsFor, setAttemptsFor] = useState(null);
  const [callingId, setCallingId] = useState(null);
  const numbers = useCampaignPhoneNumbers(campaignId);
  const campaignLanguage = subProjects?.find(item => String(item.id) === String(campaign?.subProjectId));
  const {getExtension} = useAmiExtensions(campaignLanguage?.flowUid || campaign?.flowUid);
  const confirmDelete = useConfirm("Remove phone number?", "The number and its call attempts will be deleted.", "Remove");
  const confirmCall = useConfirm("Call this number now?", "This places a call right away, outside of the campaign's schedule.", "Call");

  const fetchCampaign = () => {
    CampaignRepository.get(campaignId)
      .then(response => setCampaign(response?.data))
      .catch(() => {
        toast.error("Campaign not found");
        onBack();
      });
  };

  useEffect(() => {
    if (campaignId) fetchCampaign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const refresh = () => {
    fetchCampaign();
    numbers.refresh();
  };

  // Keep refreshing while any call on this page is ringing
  usePolling(refresh, numbers.rows.some(row => row.status === "ACTIVE"), ACTIVE_POLL_INTERVAL);

  const handleCall = async (row) => {
    const confirmed = await confirmCall({title: `Call ${row.phoneNumber} now?`});
    if (!confirmed) return;
    setCallingId(row.id);
    CampaignRepository.callPhoneNumber(campaignId, row.id)
      .then(response => {
        if (response?.data?.status === "FAILED") {
          toast.error(response?.data?.errorMessage || "The call could not be placed");
        } else {
          toast.success(`Calling ${row.phoneNumber}...`);
        }
        refresh();
      })
      .catch(error => toast.error(callErrorMessage(error)))
      .finally(() => setCallingId(null));
  };

  const handleDelete = async (row) => {
    const confirmed = await confirmDelete({title: `Remove ${row.phoneNumber} from the campaign?`});
    if (!confirmed) return;
    CampaignRepository.deletePhoneNumber(campaignId, row.id)
      .then(() => {
        toast.success("Phone number removed");
        refresh();
      })
      .catch(() => toast.error("Failed to remove phone number"));
  };

  if (campaign === null) {
    return <LoadingScreen/>
  }

  return (
    <Box sx={{height: "100%", width: "100%", maxWidth: {md: "calc(100% - 128px)"}, mx: "auto", display: "flex", flexDirection: "column", gap: 2, p: {xs: 2, md: 3}, boxSizing: "border-box"}}>
      <CampaignFormDialogNew
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        projectId={registryProjectId}
        subProjects={subProjects}
        campaign={campaign}
        onSaved={refresh}
      />
      <CallAttemptsDialogNew
        campaignId={campaignId}
        phoneNumber={attemptsFor}
        projectId={registryProjectId}
        onClose={() => setAttemptsFor(null)}
      />

      <CampaignDetailsHeaderNew campaign={campaign} onBack={onBack} onRefresh={refresh} onEdit={() => setOpenEdit(true)}/>
      <CampaignSummaryNew campaign={campaign} extension={getExtension(campaign.extensionId)}/>

      <Stack direction="row" alignItems="center" gap={1.5}>
        <Typography sx={{fontSize: 13, color: "#6B6B80"}}>Filter by status:</Typography>
        <CampaignStatusChipsNew
          statusCounts={campaign.statusCounts}
          selected={numbers.status}
          onSelect={numbers.setStatus}
        />
      </Stack>

      <CampaignPhoneNumbersTableNew
        rows={numbers.rows}
        total={numbers.total}
        loading={numbers.loading}
        page={numbers.page}
        rowsPerPage={numbers.rowsPerPage}
        onPageChange={numbers.setPage}
        onRowsPerPageChange={numbers.setRowsPerPage}
        maxAttempts={campaign.maxAttempts}
        projectId={registryProjectId}
        callingId={callingId}
        onCall={handleCall}
        onShowAttempts={setAttemptsFor}
        onDelete={handleDelete}
      />
    </Box>
  )
}
